import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../auth/useAuth";
import {
  TRACKER_STATUSES,
  createTrackerItem,
  fetchTracker,
  promoteToWorkOrder,
  updateTrackerItem,
  type TrackerItem,
  type TrackerStatus,
  type TrackerType,
} from "../lib/tracker";

// The admin's authoring + lifecycle surface for the Claude-Integrated Dev tracker (Phase 0, extended
// 2026-07-08). Draft/author requirements, defects, enhancements, and work orders; promote items into
// a work order; and drive each item through its lifecycle (approve → implement, submit, decline,
// edit). The point-and-click intake (RED Mode) and the triage/implement agents feed the same store.

const FILE_TYPES: TrackerType[] = ["requirement", "defect", "enhancement", "work-order"];
// Statuses the admin sets by hand. in-progress / in-review / done are owned by the implement run and
// the PR merge — shown when an item is already there, but never a manual authoring choice.
const HUMAN_STATUSES: TrackerStatus[] = [
  "draft",
  "new",
  "triaged",
  "designed",
  "approved",
  "declined",
];
const TYPE_LABEL: Record<TrackerType, string> = {
  requirement: "Requirement",
  defect: "Defect",
  enhancement: "Enhancement",
  feedback: "Feedback",
  "review-batch": "Review batch",
  "work-order": "Work order",
};

/** Fold optional acceptance criteria into the single body field the implement agent reads. */
function composeBody(body: string, accept: string): string {
  const b = body.trim();
  const a = accept.trim();
  if (!a) return b;
  return `${b}${b ? "\n\n" : ""}Acceptance criteria:\n${a}`;
}

function Notice({ body }: { body: string }) {
  return (
    <div className="wrap section">
      <p className="eyebrow">Admin · tracker</p>
      <h1 className="display" style={{ fontSize: 34, marginTop: 8 }}>
        Tracker
      </h1>
      <p className="section-sub">{body}</p>
    </div>
  );
}

type Patch = { status?: TrackerStatus; note?: string; title?: string; body?: string };

function ItemRow({
  item,
  selected,
  onToggleSelect,
  onSave,
}: {
  item: TrackerItem;
  selected: boolean;
  onToggleSelect: (id: string) => void;
  onSave: (id: string, patch: Patch) => Promise<boolean>;
}) {
  const [openRow, setOpenRow] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<TrackerStatus | null>(null);
  const [note, setNote] = useState("");
  const [editing, setEditing] = useState(false);
  const [eTitle, setETitle] = useState(item.title);
  const [eBody, setEBody] = useState(item.body);
  const [saving, setSaving] = useState(false);
  const [rowErr, setRowErr] = useState<string | null>(null);

  // Show the server's status unless the operator has picked a different one this session; a
  // successful save clears the override so the control re-syncs to truth after the reload.
  const status = pendingStatus ?? item.status;
  const ctx = item.context;
  const isWorkOrder = item.type === "work-order";
  const terminal = item.status === "done" || item.status === "declined";
  // Promote eligibility mirrors the server guard exactly: an unlinked report item in an early state.
  const canPromote =
    !isWorkOrder &&
    !item.workOrder &&
    (item.status === "draft" || item.status === "new" || item.status === "triaged");
  const statusChanged = status !== item.status;
  const dirty = statusChanged || note.trim().length > 0;
  // Work orders never offer new/triaged by hand — a triage-eligible work order gets re-ingested and
  // clustered into another work order. r/d/e keep the full set.
  const base = isWorkOrder
    ? (["draft", "designed", "approved", "declined"] as TrackerStatus[])
    : HUMAN_STATUSES;
  const statusOptions = base.includes(item.status) ? base : [item.status, ...base];

  const run = (patch: Patch, onOk?: () => void) => {
    setSaving(true);
    void onSave(item.entityId, patch).then((ok) => {
      setSaving(false);
      if (ok) {
        setNote("");
        setPendingStatus(null);
        setRowErr(null);
        onOk?.();
      } else {
        setRowErr("Save failed — try again.");
      }
    });
  };

  return (
    <div className={`trk-item trk-st-${item.status}`}>
      <div className="trk-rowline">
        <span className="trk-lead">
          {canPromote && (
            <input
              type="checkbox"
              className="trk-check"
              checked={selected}
              onChange={() => onToggleSelect(item.entityId)}
              aria-label={`Select "${item.title}" to promote`}
            />
          )}
        </span>
        <button type="button" className="trk-row" onClick={() => setOpenRow((o) => !o)}>
          <span className={`trk-badge mono trk-${item.type}`}>{item.type}</span>
          <span className="trk-title">{item.title}</span>
          {(item.occurrenceCount ?? item.occurrences.length) > 1 && (
            <span className="trk-count mono">×{item.occurrenceCount ?? item.occurrences.length}</span>
          )}
          <span className="trk-status mono">{item.status}</span>
        </button>
      </div>
      {openRow && (
        <div className="trk-detail">
          {editing ? (
            <div className="trk-edit">
              <input
                className="jfb-input"
                value={eTitle}
                onChange={(e) => setETitle(e.target.value)}
                aria-label="Title"
              />
              <textarea
                className="jfb-input trk-textarea"
                value={eBody}
                onChange={(e) => setEBody(e.target.value)}
                aria-label="Body"
              />
              <div className="trk-actions">
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  disabled={!eTitle.trim() || saving}
                  onClick={() =>
                    run({ title: eTitle.trim(), body: eBody.trim() }, () => setEditing(false))
                  }
                >
                  Save edits
                </button>
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => {
                    setEditing(false);
                    setETitle(item.title);
                    setEBody(item.body);
                  }}
                >
                  Cancel
                </button>
                {rowErr && <span className="mono trk-note">{rowErr}</span>}
              </div>
            </div>
          ) : (
            <p className="trk-body">{item.body || "(no detail)"}</p>
          )}

          {ctx && (
            <p className="trk-ctx mono">
              {String(ctx.route ?? "?")} · {String(ctx.component ?? "?")} ·{" "}
              {String(ctx.selector ?? "?")} · {String(ctx.version ?? "?")}
            </p>
          )}
          {item.regressionOf && <p className="trk-ctx mono">regression of {item.regressionOf}</p>}
          {item.workOrder && <p className="trk-ctx mono">in work order {item.workOrder}</p>}
          {item.memberIds && item.memberIds.length > 0 && (
            <p className="trk-ctx mono">covers: {item.memberIds.join(" · ")}</p>
          )}
          {item.annotations && item.annotations.length > 0 && (
            <ol className="trk-ann">
              {item.annotations.map((a, i) => (
                <li key={i}>
                  <span>{a.msg || "(no note)"}</span>
                  <span className="mono trk-ctx">
                    {String(a.context?.component ?? "?")} · {String(a.context?.route ?? "?")} ·{" "}
                    {String(a.context?.selector ?? "?")}
                  </span>
                </li>
              ))}
            </ol>
          )}
          {item.occurrences.length > 0 && (
            <ul className="trk-occ">
              {item.occurrences.slice(-5).map((o, i) => (
                <li key={`${o.at}-${i}`} className="mono">
                  {o.at.slice(0, 16).replace("T", " ")} — {o.msg.slice(0, 120)}
                </li>
              ))}
            </ul>
          )}

          {!editing && (
            <div className="trk-quick">
              {isWorkOrder && item.status === "designed" && (
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  disabled={saving}
                  onClick={() => run({ status: "approved" })}
                >
                  Approve for implement
                </button>
              )}
              {item.status === "draft" && (
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  disabled={saving}
                  onClick={() => run({ status: isWorkOrder ? "designed" : "new" })}
                >
                  Submit
                </button>
              )}
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => {
                  setETitle(item.title);
                  setEBody(item.body);
                  setEditing(true);
                }}
              >
                Edit
              </button>
              {!terminal && (
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={saving}
                  onClick={() =>
                    run({ status: "declined", ...(note.trim() ? { note: note.trim() } : {}) })
                  }
                >
                  Decline
                </button>
              )}
            </div>
          )}

          {!editing && (
            <div className="trk-actions">
              <select
                className="jfb-input trk-select"
                value={status}
                onChange={(e) => setPendingStatus(e.target.value as TrackerStatus)}
                aria-label="Status"
              >
                {statusOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <input
                className="jfb-input"
                placeholder="Comment (optional)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-sm btn-primary"
                disabled={!dirty || saving}
                onClick={() =>
                  run({
                    ...(statusChanged ? { status } : {}),
                    ...(note.trim() ? { note: note.trim() } : {}),
                  })
                }
              >
                Save
              </button>
              {rowErr && <span className="mono trk-note">{rowErr}</span>}
            </div>
          )}

          {item.history.length > 0 && (
            <ul className="trk-hist">
              {item.history.slice(-4).map((h, i) => (
                <li key={`${h.at}-${i}`} className="mono">
                  {h.at.slice(0, 16).replace("T", " ")} · {h.actor} — {h.note}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export function TrackerAdmin() {
  const { isAdmin, ready, getApiToken } = useAuth();
  const [items, setItems] = useState<TrackerItem[] | null>(null);
  const [state, setState] = useState<"loading" | "idle" | "error">("loading");
  const [refreshErr, setRefreshErr] = useState(false);

  // Compose (author/draft) form.
  const [fType, setFType] = useState<TrackerType>("enhancement");
  const [fTitle, setFTitle] = useState("");
  const [fBody, setFBody] = useState("");
  const [fAccept, setFAccept] = useState("");
  const [filing, setFiling] = useState(false);
  const [fileErr, setFileErr] = useState<string | null>(null);

  // Filters + promote selection.
  const [filterType, setFilterType] = useState<TrackerType | "all">("all");
  const [filterStatus, setFilterStatus] = useState<TrackerStatus | "all">("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pTitle, setPTitle] = useState("");
  const [pBody, setPBody] = useState("");
  const [promoting, setPromoting] = useState(false);
  const [promoteErr, setPromoteErr] = useState<string | null>(null);

  const reload = async (): Promise<boolean> => {
    const token = await getApiToken();
    const list = token ? await fetchTracker(token) : null;
    if (list) {
      setItems(list);
      setState("idle");
      setRefreshErr(false);
      return true;
    }
    // A post-write refresh failure must not blank the page — keep what we have and flag it inline.
    // Only a first-load failure (no items yet) shows the full error state.
    if (items) setRefreshErr(true);
    else setState("error");
    return false;
  };

  // Wait for MSAL session restore before fetching (same race as ModelSettings, fixed 2026-07-07).
  useEffect(() => {
    if (!ready || !isAdmin) return;
    let live = true;
    void (async () => {
      const token = await getApiToken();
      const list = token ? await fetchTracker(token) : null;
      if (!live) return;
      if (list) {
        setItems(list);
        setState("idle");
      } else {
        setState("error");
      }
    })();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, isAdmin]);

  const filtered = useMemo(() => {
    if (!items) return [];
    return items.filter(
      (it) =>
        (filterType === "all" || it.type === filterType) &&
        (filterStatus === "all" || it.status === filterStatus),
    );
  }, [items, filterType, filterStatus]);

  // Effective promote set: only ids that are still promotable right now (the server enforces the same
  // rule). A selected row that became terminal, got claimed, or was filtered out drops out here even
  // if its id lingers in `selected`, so the bar and the payload never act on an unseen item.
  const activeSelection = useMemo(() => {
    if (!items) return [] as string[];
    const eligible = new Set(
      items
        .filter(
          (it) =>
            it.type !== "work-order" &&
            !it.workOrder &&
            (it.status === "draft" || it.status === "new" || it.status === "triaged"),
        )
        .map((it) => it.entityId),
    );
    return [...selected].filter((id) => eligible.has(id));
  }, [items, selected]);

  if (!isAdmin) return <Notice body="This page is for the site admin. Please sign in." />;

  const file = (submit: boolean) => {
    if (!fTitle.trim()) return;
    setFiling(true);
    void (async () => {
      const token = await getApiToken();
      const status: TrackerStatus = !submit
        ? "draft"
        : fType === "work-order"
          ? "designed"
          : "new";
      const ok = token
        ? await createTrackerItem(token, {
            type: fType,
            title: fTitle.trim(),
            body: composeBody(fBody, fType === "work-order" ? fAccept : ""),
            status,
          })
        : false;
      if (ok) {
        setFTitle("");
        setFBody("");
        setFAccept("");
        setFileErr(null);
        await reload();
      } else {
        setFileErr("Couldn't save the item — try again.");
      }
      setFiling(false);
    })();
  };

  const save = async (id: string, patch: Patch) => {
    const token = await getApiToken();
    const ok = token ? await updateTrackerItem(token, id, patch) : false;
    if (ok) await reload();
    return ok;
  };

  const toggleSelect = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const promote = () => {
    if (!pTitle.trim() || activeSelection.length === 0) return;
    setPromoting(true);
    setPromoteErr(null);
    void (async () => {
      const token = await getApiToken();
      const ok = token
        ? await promoteToWorkOrder(token, {
            memberIds: activeSelection,
            title: pTitle.trim(),
            body: pBody.trim(),
            status: "designed",
          })
        : false;
      if (ok) {
        setSelected(new Set());
        setPTitle("");
        setPBody("");
        await reload();
      } else {
        setPromoteErr("Promote failed — try again.");
      }
      setPromoting(false);
    })();
  };

  return (
    <div className="wrap section" data-c="TrackerAdmin">
      <p className="eyebrow">Admin · tracker</p>
      <h1 className="display" style={{ fontSize: 34, marginTop: 8 }}>
        Tracker
      </h1>
      <p className="section-sub">
        Author requirements, defects, enhancements, and work orders — the system of record the triage
        and implement agents work from. A work order set to <span className="mono">approved</span> is
        what the implement run picks up. Same-element bug reports fold together automatically.
      </p>

      <form
        className="trk-compose"
        onSubmit={(e) => {
          e.preventDefault();
          file(true);
        }}
      >
        <div className="trk-compose-head">
          <span className="eyebrow">New item</span>
          <select
            className="jfb-input trk-select"
            value={fType}
            onChange={(e) => setFType(e.target.value as TrackerType)}
            aria-label="Item type"
          >
            {FILE_TYPES.map((t) => (
              <option key={t} value={t}>
                {TYPE_LABEL[t]}
              </option>
            ))}
          </select>
        </div>
        <div className="trk-field">
          <span className="trk-lbl mono">Title</span>
          <input
            className="jfb-input"
            placeholder="One-line summary"
            value={fTitle}
            onChange={(e) => setFTitle(e.target.value)}
          />
        </div>
        <div className="trk-field">
          <span className="trk-lbl mono">
            {fType === "work-order" ? "Design / diagnosis" : "Detail"}
          </span>
          <textarea
            className="jfb-input trk-textarea"
            placeholder={
              fType === "work-order"
                ? "What to change and why"
                : "Optional detail"
            }
            value={fBody}
            onChange={(e) => setFBody(e.target.value)}
          />
        </div>
        {fType === "work-order" && (
          <div className="trk-field">
            <span className="trk-lbl mono">Acceptance criteria</span>
            <textarea
              className="jfb-input trk-textarea"
              placeholder="The definition of done the implement run must satisfy"
              value={fAccept}
              onChange={(e) => setFAccept(e.target.value)}
            />
          </div>
        )}
        <div className="trk-compose-btns">
          <button
            type="button"
            className="btn btn-sm"
            disabled={!fTitle.trim() || filing}
            onClick={() => file(false)}
          >
            Save draft
          </button>
          <button type="submit" className="btn btn-sm btn-primary" disabled={!fTitle.trim() || filing}>
            Submit
          </button>
          {fileErr && <span className="mono trk-note">{fileErr}</span>}
        </div>
      </form>

      <div className="trk-filters">
        <span className="trk-lbl mono">Filter</span>
        <select
          className="jfb-input trk-select"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value as TrackerType | "all")}
          aria-label="Filter by type"
        >
          <option value="all">All types</option>
          {(Object.keys(TYPE_LABEL) as TrackerType[]).map((t) => (
            <option key={t} value={t}>
              {TYPE_LABEL[t]}
            </option>
          ))}
        </select>
        <select
          className="jfb-input trk-select"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as TrackerStatus | "all")}
          aria-label="Filter by status"
        >
          <option value="all">All statuses</option>
          {TRACKER_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {activeSelection.length > 0 && (
        <div className="trk-promote">
          <div className="trk-promote-head">
            <span className="eyebrow">
              Promote {activeSelection.length} {activeSelection.length === 1 ? "item" : "items"} to a
              work order
            </span>
            <button type="button" className="btn btn-sm" onClick={() => setSelected(new Set())}>
              Clear selection
            </button>
          </div>
          <input
            className="jfb-input"
            placeholder="Work order title"
            value={pTitle}
            onChange={(e) => setPTitle(e.target.value)}
          />
          <textarea
            className="jfb-input trk-textarea"
            placeholder="Design + acceptance criteria (the implement run reads this)"
            value={pBody}
            onChange={(e) => setPBody(e.target.value)}
          />
          <div className="trk-compose-btns">
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={!pTitle.trim() || promoting}
              onClick={promote}
            >
              Promote to work order
            </button>
            {promoteErr && <span className="mono trk-note">{promoteErr}</span>}
          </div>
        </div>
      )}

      {state === "loading" && <p className="mono trk-note">Loading…</p>}
      {state === "error" && (
        <p className="mono trk-note">Couldn't load the tracker — check the API and try again.</p>
      )}
      {refreshErr && (
        <p className="mono trk-note">
          Saved, but couldn't refresh the list — reload the page to see the latest.
        </p>
      )}
      {items && items.length === 0 && state === "idle" && (
        <p className="mono trk-note">Nothing tracked yet.</p>
      )}
      {items && items.length > 0 && filtered.length === 0 && (
        <p className="mono trk-note">No items match the current filter.</p>
      )}
      {filtered.length > 0 && (
        <div className="trk-list">
          {filtered.map((it) => (
            <ItemRow
              key={it.entityId}
              item={it}
              selected={selected.has(it.entityId)}
              onToggleSelect={toggleSelect}
              onSave={save}
            />
          ))}
        </div>
      )}
    </div>
  );
}
